/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {AgentAssistApiClientProvider as DefaultApiClientProvider_} from '@agent-assist/api-typescript-react-query-hooks';
import {MutationCache, QueryClient} from '@tanstack/react-query';
import isArray from 'lodash/isArray';
import startCase from 'lodash/startCase';
import React, {useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {useNotificationContext} from './NotificationContextProvider';
import {errorTitle, isApiError, useDefaultApiClient} from '../hooks/useDefaultApiClient';

const DefaultApiClientProvider: React.FC<any> = ({children}) => {
  const client = useDefaultApiClient();
  const {setNotifications} = useNotificationContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // These options can be overridden on a per-query basis
            refetchOnWindowFocus: false, // Do not refetch all queries on tab/window focus
            refetchOnReconnect: true, // Do refetch all queries when network reconnects from disconnected state
            staleTime: 0, // Always fetch fresh data on component mount
            notifyOnChangeProps: ['data', 'error'], // Only re-render if `data` or `error` changes
          },
        },
        mutationCache: new MutationCache({
          onError: (e: unknown, _variables, _context, mutation) => {
            console.error(e, mutation);

            // When an error occurs for a mutation, extract its details and display a notification
            const apiError = isApiError(e) ? e : undefined;
            const header = apiError ? errorTitle(apiError.status) : 'Error';
            const content = apiError
              ? apiError.details.message
              : `An unexpected error occurred for operation ${
                  mutation.options.mutationKey && isArray(mutation.options.mutationKey)
                    ? startCase(mutation.options.mutationKey?.[0] as string)
                    : ''
                }`;

            const notificationId = uuid();
            setNotifications((prev) => {
              if (prev.some((n) => n.header === header && n.content === content)) {
                return prev;
              }

              return [
                ...prev,
                {
                  id: notificationId,
                  type: 'error',
                  header,
                  content,
                },
              ];
            });
          },
        }),
      }),
    [setNotifications],
  );

  return client ? (
    <DefaultApiClientProvider_ apiClient={client} client={queryClient}>
      {children}
    </DefaultApiClientProvider_>
  ) : (
    <></>
  );
};

export default DefaultApiClientProvider;
